import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Gift, Target, Check, Mail, UserPlus, ChevronRight, Loader2, Sparkles, Users, Bell, PlayCircle, Trophy, Compass } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";

const Card = ({ children, testId, className = "" }) => <div data-testid={testId} className={`bg-white rounded-3xl p-5 border border-pink-100 shadow-sm ${className}`}>{children}</div>;
const Title = ({ icon: Icon, children, to, linkText }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="font-bold flex items-center gap-2"><Icon className="w-4 h-4 text-rose-500" /> {children}</h3>
    {to && <Link to={to} className="text-xs font-semibold text-rose-600 hover:underline flex items-center">{linkText}<ChevronRight className="w-3.5 h-3.5" /></Link>}
  </div>
);

export function useMissions() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/missions").then(({ data }) => { setData(data); if (data.stardust_gained) toast.success(`+${data.stardust_gained} Sternenstaub für den Sternenhimmel ✨`, { description: "Für deine geschafften Tagesmissionen" }); }).catch(() => {}), []);
  useEffect(() => {
    load();
    window.addEventListener("points-changed", load);
    return () => window.removeEventListener("points-changed", load);
  }, [load]);
  return { data, load };
}

export const PointsWidget = ({ m }) => {
  const r = m?.next_reward;
  const pct = r ? Math.min(100, Math.round(((r.cost - r.missing) / r.cost) * 100)) : 0;
  return (
    <div data-testid="widget-points" className="rounded-3xl p-5 text-white bg-gradient-to-br from-rose-500 via-pink-500 to-fuchsia-500 shadow-xl shadow-rose-200 space-y-4">
      <div className="flex items-end justify-between">
        <div><p className="text-[11px] uppercase font-bold tracking-wider opacity-80">Punktestand</p><p className="font-display text-4xl font-extrabold" data-testid="widget-points-total">{m?.points ?? "…"}</p></div>
        <div className="text-right"><p className="text-[11px] uppercase font-bold tracking-wider opacity-80">Heute</p><p className="font-display text-xl font-bold" data-testid="widget-points-today">+{m?.today_points ?? 0}</p></div>
      </div>
      {r && (
        <Link to="/geschenke" className="block space-y-1.5" data-testid="widget-next-reward">
          <div className="flex justify-between gap-2 text-xs font-semibold"><span className="truncate flex items-center gap-1"><Gift className="w-3.5 h-3.5 shrink-0" /> {r.title}</span><span className="shrink-0">{r.missing ? `noch ${r.missing} P.` : "einlösbar! 🎉"}</span></div>
          <div className="h-2 rounded-full bg-white/25"><div className="h-full rounded-full bg-white transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
        </Link>
      )}
    </div>
  );
};

export const MissionsWidget = ({ m, reload, prefix = "" }) => {
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!m) return <Card testId={`${prefix}widget-missions`}><Loader2 className="w-5 h-5 animate-spin text-rose-500 mx-auto" /></Card>;
  const claim = async () => {
    setBusy(true);
    try { const { data } = await api.post("/missions/claim"); toast.success(`Alle Missionen geschafft! +${data.earned} Punkte 🎉`); reload(); refreshUser(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const done = m.missions.filter((x) => x.done).length;
  const open = m.missions.filter((x) => !x.done);
  if (m.all_done && m.bonus_claimed) return <MissionsDone prefix={prefix} />;
  return (
    <Card testId={`${prefix}widget-missions`}>
      <Title icon={Target}>{m.weekend ? "Wochenend-Missionen" : "Tägliche Missionen"} <span className="text-xs font-semibold text-slate-400">{done}/{m.missions.length}</span></Title>
      {open.length > 0
        ? <ul className="space-y-2">
            {open.map((x) => (
              <li key={x.id} className="fade-up"><Link to={x.url} data-testid={`${prefix}mission-${x.id}`} className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-rose-50 transition-colors">
                <span className="w-6 h-6 rounded-full shrink-0 border-2 border-pink-200" />
                <span className="text-sm flex-1 font-medium">{x.title}</span>
                {x.goal > 1 && <span className="text-xs text-slate-400">{x.progress}/{x.goal}</span>}
              </Link></li>
            ))}
          </ul>
        : <p className="text-sm font-semibold text-emerald-600 flex items-center gap-1.5" data-testid={`${prefix}missions-all-done`}><Check className="w-4 h-4" /> Alle Missionen geschafft – hol dir deinen Bonus!</p>}
      {m.bonus_claimed
        ? <p className="mt-3 text-center text-sm font-semibold text-emerald-600" data-testid={`${prefix}missions-bonus-claimed`}>Bonus heute abgeholt ✓</p>
        : <button onClick={claim} disabled={!m.all_done || busy} data-testid={`${prefix}missions-claim-btn`} className="mt-3 w-full h-11 rounded-full pink-gradient text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} +{m.bonus} Bonus abholen
          </button>}
    </Card>
  );
};

const untilMidnight = () => {
  const now = new Date();
  const berlin = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Berlin" }));
  const next = new Date(berlin); next.setHours(24, 0, 0, 0);
  return Math.max(0, Math.floor((next - berlin) / 1000));
};

function MissionsDone({ prefix }) {
  const [sec, setSec] = useState(untilMidnight);
  useEffect(() => { const t = setInterval(() => setSec(untilMidnight()), 1000); return () => clearInterval(t); }, []);
  const p = (n) => String(n).padStart(2, "0");
  return (
    <div className="mission-glow relative overflow-hidden rounded-3xl p-5 text-center bg-gradient-to-br from-amber-50 via-pink-50 to-fuchsia-50 border border-pink-200" data-testid={`${prefix}missions-done-hint`}>
      <span className="mx-auto w-12 h-12 rounded-full pink-gradient text-white flex items-center justify-center shadow-lg shadow-rose-300/60"><Sparkles className="w-6 h-6" /></span>
      <p className="mt-3 font-display font-extrabold text-lg">Alle Missionen geschafft! 🎉</p>
      <p className="text-sm text-slate-600">Morgen warten neue Missionen auf dich.</p>
      <p className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-rose-600 text-xs font-bold tabular-nums" data-testid={`${prefix}missions-next-in`}>Neue Missionen in {p(Math.floor(sec / 3600))}:{p(Math.floor((sec % 3600) / 60))}:{p(sec % 60)}</p>
    </div>
  );
}

export function SocialWidget() {
  const [req, setReq] = useState([]);
  const [convs, setConvs] = useState([]);
  useEffect(() => {
    api.get("/friends/requests").then(({ data }) => setReq(data.incoming)).catch(() => {});
    const loadConvs = () => api.get("/messages").then(({ data }) => setConvs(data.slice(0, 3))).catch(() => {});
    loadConvs();
    window.addEventListener("messages-read", loadConvs);
    return () => window.removeEventListener("messages-read", loadConvs);
  }, []);
  return (
    <Card testId="widget-social" className="space-y-5">
      <div>
        <Title icon={UserPlus} to="/community?tab=requests" linkText="Alle">Freundschaftsanfragen {req.length > 0 && <span className="px-1.5 rounded-full bg-amber-400 text-amber-950 text-[11px]">{req.length}</span>}</Title>
        {req.length === 0 ? <p className="text-xs text-slate-400">Keine offenen Anfragen.</p> : req.slice(0, 3).map((u) => (
          <Link key={u.user_id} to={`/u/${u.user_id}`} className="flex items-center gap-2 py-1.5" data-testid={`widget-request-${u.user_id}`}><UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-8 h-8" /><span className="text-sm font-medium truncate">{u.name}</span></Link>
        ))}
      </div>
      <div>
        <Title icon={Mail} to="/nachrichten" linkText="Alle">Nachrichten</Title>
        {convs.length === 0 ? <p className="text-xs text-slate-400">Noch keine Unterhaltungen – schreib einem Freund über sein Profil.</p> : convs.map((c) => (
          <Link key={c.user.user_id} to={`/nachrichten/${c.user.user_id}`} className="flex items-center gap-2 py-1.5" data-testid={`widget-conv-${c.user.user_id}`}>
            <UserAvatar userId={c.user.user_id} name={c.user.name} picture={c.user.picture} size="w-8 h-8" />
            <span className="min-w-0 flex-1"><span className="block text-sm font-medium truncate">{c.user.name}</span><span className={`block text-xs truncate ${c.unread ? "text-slate-800 font-semibold" : "text-slate-400"}`}>{c.last_mine && "Du: "}{c.last_text}</span></span>
            {c.unread > 0 && <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />}
          </Link>
        ))}
      </div>
    </Card>
  );
}

const SHORTCUTS = [
  ["/community?tab=friends", Users, "Freunde", "friends"], ["/nachrichten", Mail, "Nachrichten", "messages"], ["/mitteilungen", Bell, "Mitteilungen", "notifications"],
  ["/community", Compass, "Entdecken", "discover"], ["/geschenke", Gift, "Prämien", "rewards"],
  ["/rangliste", Trophy, "Top 10", "leaderboard"], ["/einladen", UserPlus, "Freunde einladen", "invite"],
];

export function LeftNav() {
  const { user } = useAuth();
  return (
    <aside className="hidden min-[1360px]:block sticky top-24 space-y-1" data-testid="feed-leftnav">
      <Link to={`/u/${user?.user_id}`} data-testid="leftnav-profile" className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-white transition-colors">
        <UserAvatar userId={user?.user_id} name={user?.name} picture={user?.picture} size="w-9 h-9" /><span className="font-semibold text-sm truncate">{user?.name}</span>
      </Link>
      {SHORTCUTS.map(([to, Icon, label, id]) => (
        <Link key={id} to={to} data-testid={`leftnav-${id}`} className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-white transition-colors text-sm font-medium text-slate-700">
          <span className="w-9 h-9 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center"><Icon className="w-4 h-4" /></span>{label}
        </Link>
      ))}
    </aside>
  );
}

export function Suggestions() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/community/members"), api.get(`/users/${user.user_id}/friends`), api.get("/friends/requests")]).then(([m, f, r]) => {
      const skip = new Set([user.user_id, ...f.data.map((x) => x.user_id), ...r.data.incoming.map((x) => x.user_id), ...r.data.outgoing.map((x) => x.user_id)]);
      setList(m.data.filter((x) => !skip.has(x.user_id)).slice(0, 4));
    }).catch(() => {});
  }, [user.user_id]);
  if (!list.length) return null;
  return (
    <Card testId="widget-suggestions">
      <Title icon={Users} to="/community" linkText="Mehr">Personen, die du kennen könntest</Title>
      {list.map((u) => (
        <Link key={u.user_id} to={`/u/${u.user_id}`} className="flex items-center gap-3 py-1.5" data-testid={`suggestion-${u.user_id}`}>
          <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-9 h-9" />
          <span className="text-sm font-medium truncate flex-1">{u.name}</span>
          <span className="text-xs font-semibold text-rose-600">Ansehen</span>
        </Link>
      ))}
    </Card>
  );
}

export const MobileMissionsBar = ({ m }) => m && (
  <Link to="/dashboard" data-testid="mobile-missions-bar" className="lg:hidden flex items-center gap-3 rounded-2xl p-3 bg-white border border-pink-100">
    <span className="w-10 h-10 rounded-xl pink-gradient text-white flex items-center justify-center shrink-0"><Coins className="w-5 h-5" /></span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-bold">{m.points} Punkte <span className="text-xs font-semibold text-emerald-600">+{m.today_points} heute</span></span>
      <span className="block text-xs text-slate-500 truncate">{m.next_reward ? `Noch ${m.next_reward.missing} P. bis ${m.next_reward.title}` : "Punkte & Login-Bonus ansehen"}</span>
    </span>
    <ChevronRight className="w-4 h-4 text-slate-400" />
  </Link>
);
